import React, { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CreditCard, Banknote, Check } from "lucide-react";
import { toast } from "sonner";
import DynamicField, { validateFields } from "./DynamicField";
import { useI18n, labelOf } from "@/i18n/I18nProvider";

const FALLBACK_DELIVERY = {
  title: { en: "Almost done — where do we ship?" },
  subtitle: { en: "Add delivery details, pick a payment method, and we'll send your order on WhatsApp." },
  fields: [
    { key: "address", type: "textarea", label: { en: "Full address" }, placeholder: { en: "Door no, street, landmark" }, helper: {}, required: true, active: true, sort: 0 },
    { key: "alt_phone", type: "tel", label: { en: "Alternate phone" }, placeholder: { en: "Optional" }, helper: {}, required: false, active: true, sort: 1 },
    { key: "city", type: "text", label: { en: "City" }, placeholder: { en: "Enter your city" }, helper: {}, required: true, active: true, sort: 2 },
    { key: "district", type: "text", label: { en: "District" }, placeholder: { en: "Enter your district" }, helper: {}, required: true, active: true, sort: 3 },
    { key: "state", type: "text", label: { en: "State" }, placeholder: { en: "Enter your state" }, helper: {}, required: true, active: true, sort: 4 },
    { key: "pincode", type: "text", label: { en: "Pincode" }, placeholder: { en: "Enter your pincode(6 digits)" }, helper: {}, required: true, active: true, sort: 5 },
  ],
};

export default function DeliveryStep({ value, onChange, onSubmit, total, items, settings, t, formStep }) {
  const { lang } = useI18n();
  const step = formStep || FALLBACK_DELIVERY;
  const fields = useMemo(
    () => (step.fields || []).filter((f) => f.active !== false).sort((a, b) => (a.sort || 0) - (b.sort || 0)),
    [step]
  );
  const [errors, setErrors] = useState({});
  // const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fullRow = fields.filter((f) => f.type === "textarea");
  const compact = fields.filter((f) => f.type !== "textarea");

  const codCharge = Number(settings?.cod_charge || 0);
  const totalWithCod = value.payment_method === "cod" ? total + codCharge : total;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { errors: errs, ok } = validateFields(fields, value, t);
    setErrors(errs);
    if (!ok) {
      // scroll to first error
      const firstErrKey = Object.keys(errs)[0];
      const el = document.querySelector(`[data-testid='delivery-${firstErrKey}-input']`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    // if (!consent) {
    //   toast.error(t("review.consent"));
    //   return;
    // }
    setSubmitting(true);
    try {
      await onSubmit();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to send";
      toast.error(typeof msg === "string" ? msg : "Failed to send");
      setSubmitting(false);
    }
  };

  const paymentOpts = [
    { v: "online", label: t("payment.online"), note: t("payment.online.note"), icon: CreditCard, badge: t("payment.free") },
    { v: "cod", label: t("payment.cod"), note: codCharge > 0 ? `+ ₹${codCharge}` : t("payment.cod.note"), icon: Banknote, badge: codCharge > 0 ? `+₹${codCharge}` : "" },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-7 rise pb-4">
      <div>
        <p className="overline">{t("step.delivery")}</p>
        <h2 className="editorial-h h-display mt-3 text-brand-dark">{labelOf(step.title, lang, t("delivery.title"))}</h2>
        <p className="font-sans text-base text-brand-muted mt-4 leading-relaxed">
          {labelOf(step.subtitle, lang, t("delivery.subtitle"))}
        </p>
      </div>

      {/* Delivery fields */}
      <div className="space-y-5">
        {fullRow.map((f) => (
          <DynamicField
            key={f.key}
            field={f}
            value={value[f.key]}
            onChange={(v) => onChange({ [f.key]: v })}
            error={errors[f.key]}
            testIdPrefix="delivery"
          />
        ))}
        <div className="grid sm:grid-cols-2 gap-4">
          {compact.map((f) => (
            <DynamicField
              key={f.key}
              field={f}
              value={value[f.key]}
              onChange={(v) => onChange({ [f.key]: v })}
              error={errors[f.key]}
              testIdPrefix="delivery"
            />
          ))}
        </div>
      </div>

      {/* Payment method */}
      <section>
        <p className="overline mb-3">{t("step.payment")}</p>
        <div className="space-y-3">
          {paymentOpts.map((o) => {
            const Icon = o.icon;
            const active = value.payment_method === o.v;
            return (
              <button
                key={o.v}
                type="button"
                onClick={() => onChange({ payment_method: o.v })}
                data-testid={`payment-${o.v}`}
                className={`w-full bg-white border rounded-2xl p-4 flex items-center gap-4 text-left transition-all ${
                  active ? "border-brand-primary shadow-soft" : "border-brand-line hover:border-brand-dark/40"
                }`}
              >
                <div className={`w-11 h-11 rounded-xl border flex items-center justify-center ${active ? "border-brand-primary bg-brand-primary/5" : "border-brand-line"}`}>
                  <Icon strokeWidth={1.5} className={`w-5 h-5 ${active ? "text-brand-primary" : "text-brand-dark"}`} />
                </div>
                <div className="flex-1">
                  <p className="font-display text-lg text-brand-dark leading-tight">{o.label}</p>
                  <p className="font-sans text-xs text-brand-muted mt-0.5">{o.note}</p>
                </div>
                {o.badge && (
                  <span className={`discount-badge rounded-full ${o.v === "cod" ? "!bg-brand-dark" : ""}`}>{o.badge}</span>
                )}
                {active && <Check strokeWidth={2.5} className="w-5 h-5 text-brand-primary" />}
              </button>
            );
          })}
        </div>
      </section>

      {/* Order summary */}
      <section className="bg-white border border-brand-line rounded-2xl p-5">
        <p className="overline mb-3">{t("review.items")}</p>
        <div className="space-y-2" data-testid="delivery-items-summary">
          {items.map((it, idx) => (
            <div key={idx} className="flex items-center justify-between gap-3 text-sm">
              <p className="text-brand-dark truncate min-w-0">
                {it.name} <span className="text-brand-muted">× {it.qty}</span>
              </p>
              <p className="font-semibold text-brand-dark whitespace-nowrap">₹{it.price * it.qty}</p>
            </div>
          ))}
        </div>
        <div className="border-t border-brand-line mt-3 pt-3 flex items-center justify-between">
          <p className="font-sans text-[10px] uppercase tracking-widest text-brand-muted">{t("routine.total")}</p>
          <p className="font-display text-2xl text-brand-dark" data-testid="delivery-total">₹{totalWithCod}</p>
        </div>
      </section>

      {/* Consent */}
      {/* <label className="flex items-start gap-3 cursor-pointer" data-testid="delivery-consent-row">
        <Checkbox
          checked={consent}
          onCheckedChange={(c) => setConsent(!!c)}
          data-testid="delivery-consent-checkbox"
          className="mt-1 rounded-md border-brand-line data-[state=checked]:bg-brand-primary data-[state=checked]:border-brand-primary"
        />
        <span className="font-sans text-xs text-brand-muted leading-relaxed">
          {settings?.privacy_note || t("review.consent")}
        </span>
      </label> */}

      {/* Submit (WhatsApp green) */}
      <Button
        type="submit"
        disabled={submitting}
        data-testid="delivery-confirm-btn"
        className="w-full h-14 rounded-full bg-[#25D366] hover:bg-[#1FB955] text-white font-semibold tracking-wide text-base shadow-soft disabled:opacity-50 flex items-center justify-center gap-2"
      >
        <svg viewBox="0 0 32 32" className="w-5 h-5" fill="currentColor"><path d="M16.001 2.667c-7.364 0-13.334 5.97-13.334 13.333 0 2.36.617 4.65 1.788 6.677l-1.898 6.93 7.097-1.866c1.96 1.07 4.165 1.633 6.346 1.633h.001c7.364 0 13.334-5.97 13.334-13.334S23.365 2.667 16.001 2.667zm0 24.4c-2 0-3.943-.54-5.64-1.553l-.404-.24-4.21 1.107 1.123-4.106-.263-.422a10.98 10.98 0 0 1-1.717-5.853c0-6.073 4.94-11.014 11.013-11.014s11.014 4.94 11.014 11.013S22.075 27.067 16 27.067z"/></svg>
        {submitting ? t("common.sending") : t("review.send")}
      </Button>
    </form>
  );
}
